import React, { useState, useEffect } from 'react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import Breadcrumbs from '../components/Breadcrumbs';
import { useAuth } from '../context/AuthContext';
import { useSubscription, UsageStats } from '../hooks/useSubscription';
import { initLemonSqueezy, isLemonSqueezyReady, buildCheckoutUrl, openCheckout } from '../config/lemonsqueezy';
import { getAllPlans, PlanId, Plan } from '../config/plans';

// ponytail: gateway merges these blocks best-effort; each is optional (same shape as Billing)
type ExtendedUsage = UsageStats & {
  identities?: { used: number; cap: number };
  runs?: { used: number; cap: number };
};

const Pricing: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [lemonSqueezyReady, setLemonSqueezyReady] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState<PlanId | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const { user, isAuthenticated } = useAuth();
  const { subscription, usage, isLoading, getCheckoutData, canUpgradeTo } = useSubscription();

  const plans = getAllPlans();

  // Initialize Lemon Squeezy SDK
  useEffect(() => {
    const initLS = () => {
      initLemonSqueezy();
      // Check periodically until ready
      const checkReady = setInterval(() => {
        if (isLemonSqueezyReady()) {
          setLemonSqueezyReady(true);
          clearInterval(checkReady);
        }
      }, 100);

      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkReady);
        if (!isLemonSqueezyReady()) {
          console.warn('Lemon Squeezy SDK did not initialize in time');
        }
      }, 5000);
    };

    // Wait for script to load
    if (document.readyState === 'complete') {
      initLS();
    } else {
      window.addEventListener('load', initLS);
      return () => window.removeEventListener('load', initLS);
    }
  }, []);

  // Handle plan selection
  const handleSelectPlan = async (plan: Plan) => {
    if (!isAuthenticated) {
      // Redirect to login
      window.location.href = '/login';
      return;
    }

    if (plan.id === 'free') {
      // Can't "buy" free plan
      return;
    }

    if (!lemonSqueezyReady) {
      setCheckoutError('Payment system not ready. Please try again.');
      return;
    }

    setIsCheckingOut(plan.id);
    setCheckoutError(null);

    try {
      const checkoutData = await getCheckoutData(plan.id);

      if (!checkoutData) {
        setCheckoutError('Failed to initiate checkout. Please try again.');
        return;
      }

      // Build checkout URL with custom data
      const checkoutUrl = buildCheckoutUrl(checkoutData.variantId, {
        email: checkoutData.customerEmail || user?.email,
        userId: checkoutData.customData?.userId,
        planId: checkoutData.customData?.planId,
      });

      // Open Lemon Squeezy checkout overlay
      openCheckout(checkoutUrl);

    } catch (err) {
      console.error('Checkout failed:', err);
      setCheckoutError('Checkout failed. Please try again.');
    } finally {
      setIsCheckingOut(null);
    }
  };

  // Get button text and state for a plan
  const getPlanButtonState = (plan: Plan) => {
    if (!isAuthenticated) {
      return { text: 'Get Started', disabled: false, variant: 'primary' };
    }

    if (plan.id === 'free') {
      if (subscription?.planId === 'free') {
        return { text: 'Current Plan', disabled: true, variant: 'secondary' };
      }
      return { text: 'Downgrade', disabled: true, variant: 'secondary' };
    }

    if (subscription?.planId === plan.id) {
      return { text: 'Current Plan', disabled: true, variant: 'secondary' };
    }

    if (canUpgradeTo(plan.id)) {
      return { text: 'Upgrade', disabled: false, variant: 'primary' };
    }

    return { text: 'Contact Us', disabled: true, variant: 'secondary' };
  };

  return (
    <div className="h-full overflow-hidden flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-950 dark:to-slate-900 transition-colors duration-300">
      <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div className="flex flex-1 min-h-0 relative">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 overflow-y-auto relative">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Breadcrumbs */}
            <Breadcrumbs
              items={[{ label: 'Pricing' }]}
              className="mb-6"
            />

            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                Plans for agents that need real accounts
              </h1>
              <p className="text-lg text-gray-500 dark:text-slate-400 max-w-2xl mx-auto">
                Sente gives your AI agents managed email identities — send, receive, and
                verify as the identity. Upgrade or downgrade anytime.
              </p>
            </div>

            {/* Error message */}
            {checkoutError && (
              <div className="mb-8 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-center">
                <p className="text-red-600 dark:text-red-400">{checkoutError}</p>
              </div>
            )}

            {/* Current usage banner */}
            {subscription && usage && (() => {
              const extendedUsage = usage as ExtendedUsage;
              return (
                <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Current Plan: <span className="font-bold">{subscription.planName}</span>
                    </p>
                    <div className="text-right">
                      {extendedUsage.identities && (
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {extendedUsage.identities.used} / {extendedUsage.identities.cap} identities
                        </p>
                      )}
                      {extendedUsage.runs && (
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {extendedUsage.runs.used} / {extendedUsage.runs.cap} registrations & logins this period
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Pricing cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const buttonState = getPlanButtonState(plan);
                const isCurrentPlan = subscription?.planId === plan.id;
                const isCheckingOutThisPlan = isCheckingOut === plan.id;

                return (
                  <div
                    key={plan.id}
                    className={`relative bg-white dark:bg-slate-900/80 rounded-xl border overflow-hidden shadow-sm transition-all ${
                      plan.highlighted
                        ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-500/20'
                        : 'border-gray-200 dark:border-slate-700/50'
                    } ${isCurrentPlan ? 'ring-2 ring-green-500/30' : ''}`}
                  >
                    {/* Popular badge */}
                    {plan.highlighted && (
                      <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                        Most Popular
                      </div>
                    )}

                    {/* Current plan badge */}
                    {isCurrentPlan && (
                      <div className="absolute top-0 left-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-br-lg">
                        Current Plan
                      </div>
                    )}

                    <div className="p-6">
                      {/* Plan header */}
                      <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                          {plan.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-slate-400">
                          {plan.description}
                        </p>
                      </div>

                      {/* Price */}
                      <div className="mb-6">
                        <span className="text-4xl font-bold text-gray-900 dark:text-white">
                          {plan.priceMonthly === 0 ? '$0' : `$${plan.priceMonthly}`}
                        </span>
                        {plan.priceMonthly > 0 && (
                          <span className="text-gray-500 dark:text-slate-400">/month</span>
                        )}
                      </div>

                      {/* CTA Button */}
                      <button
                        onClick={() => handleSelectPlan(plan)}
                        disabled={buttonState.disabled || isCheckingOutThisPlan || isLoading}
                        className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                          buttonState.variant === 'primary'
                            ? 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 dark:disabled:bg-slate-700'
                            : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 disabled:opacity-50'
                        } disabled:cursor-not-allowed`}
                      >
                        {isCheckingOutThisPlan ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          buttonState.text
                        )}
                      </button>

                      {/* Features list */}
                      <div className="mt-6 pt-6 border-t border-gray-100 dark:border-slate-700/50">
                        <ul className="space-y-3">
                          <li className="flex items-center text-sm text-gray-600 dark:text-slate-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {plan.maxIdentities} managed identit{plan.maxIdentities !== 1 ? 'ies' : 'y'} with inbox
                          </li>
                          <li className="flex items-center text-sm text-gray-600 dark:text-slate-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {plan.emailsPerMonth.toLocaleString()} emails / month, sent & received as the identity
                          </li>
                          <li className="flex items-center text-sm text-gray-600 dark:text-slate-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {plan.runsPerMonth.toLocaleString()} registrations & logins / month
                          </li>
                          <li className="flex items-center text-sm text-gray-600 dark:text-slate-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {plan.maxConcurrentSessions} concurrent browser session{plan.maxConcurrentSessions !== 1 ? 's' : ''}
                          </li>
                          <li className="flex items-center text-sm text-gray-600 dark:text-slate-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Verification inbox: waitForOtp / waitForMagicLink
                          </li>
                          <li className="flex items-center text-sm text-gray-600 dark:text-slate-300">
                            <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Webhooks for inbound mail
                          </li>
                          {plan.features.prioritySupport && (
                            <li className="flex items-center text-sm text-gray-600 dark:text-slate-300">
                              <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Priority support
                            </li>
                          )}
                          {plan.features.teamSeats > 1 && (
                            <li className="flex items-center text-sm text-gray-600 dark:text-slate-300">
                              <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              {plan.features.teamSeats} team seats
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* FAQ or additional info */}
            <div className="mt-12 text-center">
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Questions? <a href="mailto:support@sente.run" className="text-blue-600 dark:text-blue-400 hover:underline">Contact us</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
